import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { BookingStatus } from '@/features/booking/types/bookingTypes';
import type { BookingCategoryClass } from '@/features/booking/types/bookingTypes';

interface StatusTestContextValue {
  getTestStatus: (bookingId: string) => string | null;
  setTestStatus: (bookingId: string, status: string | null) => void;
  globalTestStatus: string | null;
  setGlobalTestStatus: (status: string | null) => void;
}

const StatusTestContext = createContext<StatusTestContextValue | null>(null);

export const useStatusTest = () => {
  const context = useContext(StatusTestContext);
  if (!context) {
    // Return no-op functions if not in context (production or no provider)
    return {
      getTestStatus: () => null,
      setTestStatus: () => {},
      globalTestStatus: null,
      setGlobalTestStatus: () => {},
    };
  }
  return context;
};

// Booking Class Test Context
interface ClassTestContextValue {
  globalTestClass: BookingCategoryClass | null;
  setGlobalTestClass: (categoryClass: BookingCategoryClass | null) => void;
}

const ClassTestContext = createContext<ClassTestContextValue | null>(null);

export const useClassTest = () => {
  const context = useContext(ClassTestContext);
  if (!context) {
    return {
      globalTestClass: null,
      setGlobalTestClass: () => {},
    };
  }
  return context;
};

interface StatusTestProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for testing booking status UI in development.
 * Wraps the app to enable status overrides for any booking.
 */
export const StatusTestProvider: React.FC<StatusTestProviderProps> = ({ children }) => {
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});
  const [globalTestStatus, setGlobalTestStatus] = useState<string | null>(null);
  const [globalTestClass, setGlobalTestClass] = useState<BookingCategoryClass | null>(null);

  const getTestStatus = useCallback((bookingId: string): string | null => {
    // Global status takes precedence if set
    if (globalTestStatus) {
      return globalTestStatus;
    }
    return statusOverrides[bookingId] || null;
  }, [statusOverrides, globalTestStatus]);

  const setTestStatus = useCallback((bookingId: string, status: string | null) => {
    setStatusOverrides(prev => {
      if (status === null) {
        const { [bookingId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [bookingId]: status };
    });
  }, []);

  // Only provide context in development
  if (import.meta.env.PROD) {
    return <>{children}</>;
  }

  return (
    <StatusTestContext.Provider value={{ getTestStatus, setTestStatus, globalTestStatus, setGlobalTestStatus }}>
      <ClassTestContext.Provider value={{ globalTestClass, setGlobalTestClass }}>
        {children}
      </ClassTestContext.Provider>
    </StatusTestContext.Provider>
  );
};

const allStatuses = [
  { value: '', label: '-- Use API Status --' },
  { value: BookingStatus.ACTIVE, label: 'Active' },
  { value: BookingStatus.REDEEMED, label: 'Redeemed' },
  { value: BookingStatus.CANCELLED_BY_USER_WITH_REFUND, label: 'Cancelled (With Refund)' },
  { value: BookingStatus.CANCELLED_BY_USER_WITHOUT_REFUND, label: 'Cancelled (No Refund)' },
  { value: BookingStatus.CANCELLED_BY_BUSINESS, label: 'Cancelled by Business' },
  { value: BookingStatus.EXPIRED, label: 'Expired' },
  { value: BookingStatus.PENDING_PAYMENT, label: 'Pending Payment' },
  { value: BookingStatus.DISPUTED, label: 'Disputed' },
  { value: BookingStatus.DISPUTE_LOST, label: 'Dispute Lost (Refunded)' },
];

const allCategoryClasses: { value: BookingCategoryClass | ''; label: string }[] = [
  { value: '', label: '-- Use API Class --' },
  { value: 'Diamond', label: '💎 Diamond' },
  { value: 'Platinum', label: '⬜ Platinum' },
  { value: 'Gold', label: '🥇 Gold' },
  { value: 'Silver', label: '🥈 Silver' },
  { value: 'Bronze', label: '🥉 Bronze' },
  { value: 'Standard', label: '📋 Standard' },
];

/**
 * Floating status switcher for testing all booking statuses globally.
 * Only renders in development mode.
 */
export const GlobalStatusSwitcher: React.FC = () => {
  const { globalTestStatus, setGlobalTestStatus } = useStatusTest();
  const { globalTestClass, setGlobalTestClass } = useClassTest();

  // Only render in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <motion.div
      className="fixed bottom-4 right-4 z-[9999] p-3 rounded-lg shadow-xl"
      style={{
        background: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(139, 92, 246, 0.5)',
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-xs text-violet-400 font-semibold mb-2 uppercase tracking-wider">
        🧪 Dev Tester
      </div>
      
      {/* Status Switcher */}
      <div className="mb-2">
        <label className="text-xs text-gray-500 mb-1 block">Status</label>
        <select
          value={globalTestStatus || ''}
          onChange={(e) => setGlobalTestStatus(e.target.value || null)}
          className="w-full px-3 py-2 rounded-md text-sm font-medium cursor-pointer min-w-[180px]"
          style={{
            background: 'rgba(31, 41, 55, 0.9)',
            color: '#fff',
            border: '1px solid rgba(75, 85, 99, 0.5)',
            outline: 'none',
          }}
        >
          {allStatuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>
      
      {/* Category Class Switcher */}
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Tier/Class</label>
        <select
          value={globalTestClass || ''}
          onChange={(e) => setGlobalTestClass((e.target.value || null) as BookingCategoryClass | null)}
          className="w-full px-3 py-2 rounded-md text-sm font-medium cursor-pointer min-w-[180px]"
          style={{
            background: 'rgba(31, 41, 55, 0.9)',
            color: '#fff',
            border: '1px solid rgba(75, 85, 99, 0.5)',
            outline: 'none',
          }}
        >
          {allCategoryClasses.map((cls) => (
            <option key={cls.value} value={cls.value}>
              {cls.label}
            </option>
          ))}
        </select>
      </div>
      
      {(globalTestStatus || globalTestClass) && (
        <div className="text-xs text-amber-400 mt-2 space-y-1">
          {globalTestStatus && <div>Status: {globalTestStatus}</div>}
          {globalTestClass && <div>Class: {globalTestClass}</div>}
        </div>
      )}
    </motion.div>
  );
};
